import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { alpha } from "@mui/material/styles";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ApiIcon from "@mui/icons-material/Api";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

const visualThemes = [
  { match: ["Oracle Fusion", "Invoicing"], icon: <ApiIcon />, accent: "#4DA3FF" },
  { match: ["Accounting", "GL", "Reports"], icon: <ReceiptLongIcon />, accent: "#22c55e" },
  { match: ["SaaS", "Dashboard", "Enterprise"], icon: <DashboardCustomizeIcon />, accent: "#ec4899" },
  { match: ["Web3", "DeFi", "Wallets", "DApp"], icon: <AccountBalanceWalletIcon />, accent: "#a855f7" },
  { match: ["FinTech", "Full-Stack"], icon: <BusinessCenterIcon />, accent: "#007FFF" },
];

function getVisualTheme(tags = []) {
  return (
    visualThemes.find((theme) =>
      theme.match.some((tag) => tags.includes(tag))
    ) || visualThemes[visualThemes.length - 1]
  );
}

function ProjectVisual({ project, height = 180, variant = "card" }) {
  const { icon, accent } = getVisualTheme(project.tags);
  const compact = variant === "card";
  const title = project.title || "Project Case Study";
  const primaryTag = project.tags?.[0] || "Case Study";

  return (
    <Box
      role="img"
      aria-label={project.imageAlt || `${title} project preview`}
      sx={{
        height,
        position: "relative",
        overflow: "hidden",
        p: compact ? 2 : { xs: 2.5, md: 4 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${alpha(accent, 0.2)} 0%, rgba(10, 10, 15, 0.96) 58%, rgba(0, 212, 170, 0.12) 100%)`,
        borderBottom: (theme) =>
          variant === "card" ? `1px solid ${alpha(theme.palette.divider, 0.12)}` : "none",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.12), transparent 26%), radial-gradient(circle at 80% 30%, rgba(0,212,170,0.14), transparent 28%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: compact ? 330 : 620,
          height: "100%",
          minHeight: compact ? 132 : 240,
          borderRadius: 2,
          backgroundColor: "rgba(8, 10, 18, 0.78)",
          border: `1px solid ${alpha(accent, 0.35)}`,
          boxShadow: `0 18px 45px ${alpha("#000000", 0.35)}`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: compact ? 22 : 32,
            px: compact ? 1 : 1.5,
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            backgroundColor: "rgba(255,255,255,0.06)",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {["#ff5f57", "#ffbd2e", "#28c840"].map((color) => (
            <Box
              key={color}
              sx={{
                width: compact ? 6 : 8,
                height: compact ? 6 : 8,
                borderRadius: "50%",
                backgroundColor: color,
              }}
            />
          ))}
          <Box
            sx={{
              ml: 1,
              flex: 1,
              height: compact ? 8 : 11,
              borderRadius: 5,
              backgroundColor: "rgba(255,255,255,0.08)",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: compact ? "74px 1fr" : "150px 1fr",
            height: `calc(100% - ${compact ? 22 : 32}px)`,
          }}
        >
          <Box
            sx={{
              p: compact ? 1 : 2,
              background: `linear-gradient(180deg, ${alpha(accent, 0.25)}, rgba(255,255,255,0.03))`,
              borderRight: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Box
              sx={{
                width: compact ? 30 : 48,
                height: compact ? 30 : 48,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accent,
                backgroundColor: alpha(accent, 0.16),
                mb: compact ? 1 : 2,
              }}
            >
              {icon}
            </Box>
            <Typography
              variant={compact ? "caption" : "h6"}
              component="p"
              sx={{
                color: "text.primary",
                fontWeight: 800,
                lineHeight: 1.2,
                display: "-webkit-box",
                WebkitLineClamp: compact ? 2 : 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {primaryTag}
            </Typography>
          </Box>

          <Box sx={{ p: compact ? 1.25 : 2.5 }}>
            <Typography
              variant={compact ? "subtitle2" : "h5"}
              component="p"
              sx={{
                color: "text.primary",
                fontWeight: 800,
                mb: compact ? 1 : 2,
                lineHeight: 1.25,
                display: "-webkit-box",
                WebkitLineClamp: compact ? 2 : 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {title}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: compact ? 0.7 : 1.2 }}>
              {[0.86, 0.68, 0.78].map((width, index) => (
                <Box
                  key={index}
                  sx={{
                    width: `${width * 100}%`,
                    height: compact ? 7 : 10,
                    borderRadius: 5,
                    backgroundColor: "rgba(255,255,255,0.12)",
                  }}
                />
              ))}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: compact ? 0.8 : 1.5,
                mt: compact ? 1.3 : 3,
              }}
            >
              {[accent, "#00D4AA"].map((color, index) => (
                <Box
                  key={color}
                  sx={{
                    height: compact ? 30 : 62,
                    borderRadius: 1,
                    background: `linear-gradient(135deg, ${alpha(color, 0.35)}, rgba(255,255,255,0.05))`,
                    border: `1px solid ${alpha(color, 0.22)}`,
                    display: { xs: index === 1 && compact ? "none" : "block", sm: "block" },
                  }}
                />
              ))}
            </Box>

            {!compact && (
              <Box sx={{ display: "flex", gap: 1, mt: 3, flexWrap: "wrap" }}>
                {project.tags?.slice(0, 3).map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{
                      backgroundColor: alpha(accent, 0.14),
                      color: accent,
                      fontWeight: 700,
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ProjectVisual;
